import React, { useEffect, useState, useMemo } from "react"
import PropTypes from "prop-types"
import _isEmpty from "lodash/isEmpty"
import _get from "lodash/get"
import AemGrid from "@/components/AemGrid"
import { useDispatch, useSelector } from "react-redux"
import useIsSsr from "@/hooks/useIsSsr"
import { useRouter } from "next/router"
import usePageAuth from "@/hooks/usePageAuth"
import {
  getDataLayerObj,
  getQuickViewAuth,
  getUserPersona,
  removeAndCleanLink,
} from "@/utils/helper"
import { getTeaserFragments } from "@/components/ProductDetail/v3/productDetailsHelper"
import { processVariantImages } from "@/utils/productImageHelper"

import {
  selectGenericState,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import { selectAuthState } from "@/store/features/authSlice"
import { selectPdpState } from "@/store/features/pdpSlice"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import ProductDetail from "@/components/ProductDetail/v3/ProductDetail"
import PairsWellWith from "@/components/PairsWellWith/PairsWellWith"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import Review from "@/components/Review/v1/Review"
import ProductExf from "@/components/ProductDetail/v3/ProductLandingTeasers/ProductExf"
import PDPLoader from "@/components/ProductDetail/v3/PDPLoader"
import NextHead from "@/components/core/Head/NextHead"
import PDPCuratedProducts from "@/components/ProductDetail/v3/ProductDetailView/PDPCuratedProducts"
import Script from "next/script"
import { SCENE7_JS_URL } from "@/components/ImageGallery/v2/constants"
import { getConfig } from "@/constants/config"

const ProductDetailsPage = props => {
  const {
    pageModelData = {},
    header,
    footer,
    ip,
    globalScript,
    footerScript,
    analyticsData,
    gbhDataLayerCustom,
    slug,
    skuId,
    type,
    pdpData = {},
    fullUrl = "",
    isPart,
    siteName = "",
    seoProperties: { email = "", phoneNum = "" } = {},
    reviewStatsData,
    fullReviewData,
  } = props || {}

  const dispatch = useDispatch()
  const router = useRouter()
  const isSsr = useIsSsr()
  const { access_token: accessToken } = useSelector(selectAuthState)
  const { pageIsInteractive } = useSelector(selectGenericState)
  const { isTeaserLoaded } = useSelector(selectPdpState)
  const [pageRedirect, setPageRedirect] = useState("")
  const { redirectTarget: { page: { path = "" } = {} } = {} } = pageModelData
  const [config, setConfig] = useState({})
  const [priceSpiderKey, setPriceSpiderKey] = useState("")
  const [priceSpiderLanguage, setPriceSpiderLanguage] = useState("")
  const [priceSpiderCountry, setPriceSpiderCountry] = useState("")

  const {
    primaryBundleSKU_s: primaryBundleSKUs = "",
    productRegionAllVariantDiscontinued_b: discontinuedProduct = false,
  } = pdpData || {}

  const isBundleProduct = Boolean(primaryBundleSKUs)

  // Process variant images using the reusable utility function
  const { variantImages, initialImgAlt, initialImgUrl, initialImageFullUrl } =
    useMemo(() => processVariantImages(pdpData, skuId), [pdpData, skuId])

  useEffect(() => {
    getConfig().then(config => setConfig(config))

    const {
      ProductLocalCategory_s: productLocalCategory = "",
      Product_Category: productCategory = "",
      CustomerFacingSKU_s: skuID = "",
      CustomerFacingBrand_s: siteName = "",
    } = pdpData || {}

    window.needPdpdata = {
      productSubCatergory:
        productLocalCategory?.toLowerCase() || productCategory?.toLowerCase(),
      skuID,
      siteName: siteName.toLowerCase(),
    }
    if (gbhDataLayerCustom) dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => dispatch(setGbhDataLayerCustom({}))
  }, [])

  useEffect(() => {
    window.adobeTarget = window.adobeTarget || {}
    window.adobeTarget.pdp = {
      variantImages: variantImages,
    }
  }, [variantImages])

  useEffect(() => {
    if (!_isEmpty(config)) {
      const psKey = _get(config, "general.priceSpiderKey", "")
      if (psKey) {
        setPriceSpiderKey(psKey)
      }
      const psLanguage = _get(config, "internationalization.language", "")
      if (psLanguage) {
        setPriceSpiderLanguage(psLanguage)
      }
      const psCountry = _get(config, "internationalization.country", "")
      if (psCountry) {
        setPriceSpiderCountry(psCountry)
      }
    }
  }, [config])

  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])

  usePageAuth()

  useEffect(() => {
    if (analyticsData) getDataLayerObj(analyticsData)
  }, [analyticsData])

  if (
    typeof window !== "undefined" &&
    window.kohler?.datalayer?.readyState === "ready"
  ) {
    const kohlerDatalayer = window.kohler.datalayer
    if (analyticsData && kohlerDatalayer?.setItem) {
      kohlerDatalayer.setItem("PAGE_INFO", analyticsData)
    }
  }

  useEffect(() => {
    if (path) {
      setPageRedirect(removeAndCleanLink(path))
    }
  }, [path])

  useEffect(() => {
    if (!isSsr && pageRedirect) {
      router.push(pageRedirect)
    }
  }, [isSsr, pageRedirect])

  useEffect(() => {
    if (pdpData && accessToken && pageIsInteractive && !isTeaserLoaded) {
      getTeaserFragments(pageModelData, pdpData)
    }
  }, [accessToken, pdpData, pageIsInteractive, isTeaserLoaded])

  const { language: metaLanguage = "" } = pageModelData

  const quickViewAuthData = getQuickViewAuth(footer)
  const productDetailKey = `${slug || pdpData?.slug_s || ""}-${
    skuId || pdpData?.masterSKU_s || ""
  }`

  return (
    <>
      <NextHead
        psKey={priceSpiderKey}
        psCountry={priceSpiderCountry}
        psLanguage={priceSpiderLanguage}
        fullUrl={fullUrl}
        metaLanguage={metaLanguage}
        pdpData={pdpData}
        reviewStatsData={reviewStatsData}
        fullReviewData={fullReviewData}
        isPart={isPart}
        initialImageFullUrl={initialImageFullUrl}
        skuId={skuId}
        siteName={siteName}
        isBundleProduct={isBundleProduct}
        persona={getUserPersona()}
        config={config}
        variantImages={variantImages}
      />
      {header ? (
        <GlobalPageHeader
          themeType={pageModelData?.appliedCssClassNames}
          header={header}
          title={pdpData?.metaTitle_s}
          description={pdpData?.metaDescription_s}
          templateName={pageModelData?.templateName}
          globalScripting={globalScript}
          isPdp={true}
          fullUrl={fullUrl}
          imageUrl={initialImageFullUrl}
          email={email}
          phone={phoneNum}
          siteName={siteName}
          data={pageModelData}
        />
      ) : null}

      <Script
        key="zoomdatapdp"
        id="zoomdatapdp"
        src={`${SCENE7_JS_URL}ZoomViewer.js`}
      />

      <Script type="text/javascript" src={`${SCENE7_JS_URL}SpinViewer.js`} />

      {pageModelData && !_isEmpty(pdpData) ? (
        <div className="min-vh-100">
          <AemGrid
            data={pageModelData}
            count={0}
            productDetail={(data, slug, skuId, type) => (
              <ProductDetail
                key={productDetailKey}
                data={data}
                pdpData={pdpData}
                slug={slug}
                skuId={skuId}
                type={type}
                initialImgAlt={initialImgAlt}
                initialImgUrl={initialImgUrl}
                analyticsData={analyticsData}
              />
            )}
            productDetailFiller={data => (
              <PDPLoader
                initialImgAlt={initialImgAlt}
                initialImgUrl={initialImgUrl}
                pdpJson={data}
                pdpData={pdpData}
                skuId={skuId}
              />
            )}
            pairsWellWith={data => <PairsWellWith data={data} skuId={skuId} />}
            productAccordion={data => <ProductAccordion data={data} />}
            review={data => <Review data={data} />}
            productExf={data => <ProductExf data={data} />}
            slug={slug}
            skuId={skuId}
            type={type}
            pdpCuratedProduct={data =>
              !discontinuedProduct ? (
                <PDPCuratedProducts
                  authData={data}
                  slug={slug}
                  skuId={skuId}
                  quickViewAuthData={quickViewAuthData}
                />
              ) : null
            }
          />
        </div>
      ) : null}
      {footer ? (
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      ) : null}
    </>
  )
}

ProductDetailsPage.propTypes = {
  reviewStatsData: PropTypes.object,
  fullReviewData: PropTypes.object,
}

export default ProductDetailsPage
